import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AgentNewsDocument } from '../../app/home/ghostnews/agent.interface';
import { Subscription } from '../../app/home/subscriptions/page';
import { useFetchCurrentUserSubscriptionsQuery } from '../redux/services/subscriptions';

interface AgentHealthStatus {
  healthy: boolean;
  errors:
    | {
        area: string;
        field: string;
        error: string;
        severity: string;
      }[]
    | null;
}

interface AgentContextProps {
  currentAgentId: string | null;
  setCurrentAgentId: Dispatch<SetStateAction<string>>;
  agentHealthStatus?: AgentHealthStatus;
  setAgentHealthStatus: Dispatch<SetStateAction<AgentHealthStatus>>;
  currentAgent?: AgentNewsDocument;
  setCurrentAgent: Dispatch<SetStateAction<AgentNewsDocument>>;
  loadingAgent?: boolean;
  setLoadingAgent: Dispatch<SetStateAction<boolean>>;
  agentList?: AgentNewsDocument[];
  setAgentList: Dispatch<SetStateAction<AgentNewsDocument[]>>;
  agentSubscriptions?: Subscription[];
  loadingSubscriptions?: boolean;
  refetchSubscriptions: () => void;
}

export const AgentContext = createContext<AgentContextProps>({
  currentAgentId: null,
  setCurrentAgentId: () => {},
  agentHealthStatus: undefined,
  setAgentHealthStatus: (status) => {},
  currentAgent: null,
  setCurrentAgent: () => {},
  loadingAgent: false,
  setLoadingAgent: () => {},
  agentList: [],
  setAgentList: () => {},
  agentSubscriptions: [],
  loadingSubscriptions: false,
  refetchSubscriptions: () => {},
});

export const AgentContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentAgentId, setCurrentAgentId] = useState<string>();
  const [agentHealthStatus, setAgentHealthStatus] =
    useState<AgentHealthStatus>();
  const [currentAgent, setCurrentAgent] = useState<AgentNewsDocument>();
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [agentList, setAgentList] = useState<AgentNewsDocument[]>([]);

  const {
    data: subscriptions,
    isFetching: loadingSubscriptions,
    refetch: refetchSubscriptions,
  } = useFetchCurrentUserSubscriptionsQuery(
    { itemID: currentAgentId },
    {
      skip: !currentAgentId,
      pollingInterval: 5 * 60 * 1000, // 5 minutes in milliseconds
    }
  );

  useEffect(() => {
    if (currentAgentId && currentAgent) {
      setLoadingAgent(false);
    }
  }, [currentAgentId, currentAgent]);

  return (
    <AgentContext.Provider
      value={{
        currentAgentId,
        setCurrentAgentId,
        agentHealthStatus,
        setAgentHealthStatus,
        currentAgent,
        setCurrentAgent,
        loadingAgent,
        setLoadingAgent,
        agentList,
        setAgentList,
        agentSubscriptions: subscriptions?.data || [],
        loadingSubscriptions,
        refetchSubscriptions,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgentContext = () => {
  return useContext(AgentContext);
};
